import { observable } from "mobx";
import ModalModel from "gradifui/models/ModalModel";
import { dispatchable, ActionStore } from "higher-order/ActionStore";
import some from "lodash/some";
import remove from "lodash/remove";
import noop from "lodash/noop";
import upsert from "util/upsert";

const DEFAULT_FLASH_TIMEOUT = 3000;
const DEFAULT_LOADING_SPINNER_DELAY = 100;

class LoadSpinnerModel extends ModalModel {
    @observable message;

    constructor() {
        super();
    }

    open(message, delay) {
        delay = delay || DEFAULT_LOADING_SPINNER_DELAY;
        let timeStamp = (new Date()).getTime();

        setTimeout(() => {
            if (this.timeStamp === timeStamp) {
                this.message = message;
                super.open();
            }
        }, delay);

        this.timeStamp = timeStamp;
    }

    close() {
        this.timeStamp = null;
        super.close();
    }
}

class ConfirmationModalModel extends ModalModel {
    // string
    @observable message;
    // string
    @observable title;
    // FormModel
    @observable customForm;
    // (jsonFormData) => Component
    @observable customRender;
    // (onConfirmClick, confirmLabel, onCancelClick, cancelLabel) => Component
    @observable renderCustomControls;

    @observable hasCallbacks;

    constructor() {
        super("confirmationModal");
        this.confirmCallback = noop;
        this.cancelCallback = noop;
        this.hasCallbacks = false;
        this.confirmLabel = "Confirm";
        this.cancelLabel = "Cancel";
        this.shouldStayOpenAfterSubmit = false;
    }

    open(confirmEventData) {
        this.message = confirmEventData.message;
        this.title = confirmEventData.title;
        this.customForm = confirmEventData.customForm;
        this.customRender = confirmEventData.customRender;
        this.renderCustomControls = confirmEventData.renderCustomControls;
        this.renderCustomTitle = confirmEventData.renderCustomTitle;
        this.hideCloseButton = confirmEventData.hideCloseButton;
        this.hideCancelButton = confirmEventData.hideCancelButton || false;
        this.confirmCallback = confirmEventData.confirmCallback || noop;
        this.cancelCallback = confirmEventData.cancelCallback || noop;
        this.modalClassName = confirmEventData.modalClassName;
        this.confirmLabel = confirmEventData.confirmLabel || "Confirm";
        this.confirmClassName = confirmEventData.confirmClassName;
        this.cancelLabel = confirmEventData.cancelLabel || "Cancel";
        this.hasCallbacks = confirmEventData.confirmCallback || confirmEventData.cancelCallback;
        this.shouldStayOpenAfterSubmit = confirmEventData.shouldStayOpenAfterSubmit;
        this.hideControls = confirmEventData.hideControls || false;
        super.open();
    }

    confirm() {
        if (this.customForm) {
            this.customForm.allDone();
            if (this.customForm.canSubmit) {
                // By default, we close before the callback b/c the callback might be to open a new confirm
                if (!this.shouldStayOpenAfterSubmit) {
                    this.close();
                }
                this.confirmCallback();
            }
        } else {
            this.close();
            this.confirmCallback();
        }
    }

    cancel() {
        // mimic old school browser event handlers that allow cancelling "bubbling" by explicitly returning false
        if (this.cancelCallback() !== false) {
            this.close();
        }
    }

    close() {
        this.message = null;
        this.title = null;
        this.customForm = null;
        this.customRender = null;
        this.renderCustomControls = null;
        this.hideCloseButton = false;
        super.close();
    }
}

export default class MessageStore extends ActionStore {
    @observable messages;
    @observable confirmationModal;
    @observable loadSpinner;
    @observable localizedMessages;

    constructor(props, dispatcher) {
        super(dispatcher);
        const flashMessage = props.session && props.session.flashMessage;
        this.messages = flashMessage ? [{
            id: Math.floor(new Date().getTime()),
            message: flashMessage.message,
            contentKey: flashMessage.contentKey,
            timeout: flashMessage.timeout || DEFAULT_FLASH_TIMEOUT,
            className: flashMessage.className
        }] : [];
        this.confirmationModal = new ConfirmationModalModel();
        this.loadSpinner = new LoadSpinnerModel();
        this.localizedMessages = observable.map({});
    }

    @dispatchable
    showConfirmationModal(e) {
        this.confirmationModal.open(e.data);
    }

    @dispatchable
    confirmConfirmationModal(e) {
        this.confirmationModal.confirm();
    }

    @dispatchable
    cancelConfirmationModal(e) {
        this.confirmationModal.cancel();
    }

    @dispatchable
    showFlashMessage(e) {
        const currentMessages = this.messages;
        const newMessage = {
            id: e.data.messageId || Math.floor(new Date().getTime()),
            message: e.data.message,
            contentKey: e.data.contentKey,
            timeout: e.data.timeout || DEFAULT_FLASH_TIMEOUT,
            className: e.data.className,
            closeable: e.data.closeable,
            icon: e.data.icon,
            link: e.data.link,
            containerId: e.data.containerId
        };
        if (some(currentMessages, m => m.id === e.data.messageId)) {
            this.logger.notify("A flash message has duplicate id, message possibly stomped", "warning");
        }
        if (e.data.containerId) {
            if (this.localizedMessages.get(e.data.containerId)) {
                this.localizedMessages.get(e.data.containerId).push(newMessage);
            } else {
                this.localizedMessages.set(e.data.containerId, [newMessage]);
            }
        } else {
            upsert(currentMessages, newMessage, m => m.id === newMessage.id);
        }
    }

    @dispatchable
    hideFlashMessage(e) {
        const currentMessages = (e.data.containerId && this.localizedMessages[e.data.containerId]) ?
            this.localizedMessages[e.data.containerId] : this.messages;
        remove(currentMessages, m => m.id === e.data.messageId);
    }

    @dispatchable
    clearFlashMessages(e) {
        if (e.data.containerId) {
            this.localizedMessages.set(e.data.containerId, null);
        } else {
            this.messages.clear();
        }
    }

    @dispatchable
    showLoadSpinner(e) {
        this.loadSpinner.open(e.data.message, e.data.timeout);
    }

    @dispatchable
    hideLoadSpinner(e) {
        this.loadSpinner.close();
    }

    browserNavigationWarningModalCallback(event) {
        event.preventDefault();
        event.returnValue = "Are you sure? Changes you made may not be saved.";
    }

    @dispatchable
    enableNavigationWarningModal() {
        window.addEventListener("beforeunload", this.browserNavigationWarningModalCallback);
    }

    @dispatchable
    disableNavigationWarningModal() {
        window.removeEventListener("beforeunload", this.browserNavigationWarningModalCallback);
    }

}
